// LxSheet — Lamtix-token-wrapped Sheet (side panel) primitive
//
// RTL audit:
//   - side prop defaults to 'left' (physical) — in RTL layout, sidebar is at physical RIGHT
//     so the details panel slides from the LEFT (does not conflict with sidebar)
//   - width is FIXED PIXELS (not logical max-w) — max-w collapses in RTL due to padding
//     computations; fixed 600px prevents squeezing
//   - Close button at end-4 top-4 (logical) — in RTL this is physical LEFT, away from sidebar
//   - SheetHeader text-start (logical, RTL-safe)
//   - No raw Tailwind colors — semantic only
//
// D-UI-LOCK-01: Sidebar is always at physical RIGHT (right: 0).
//   Sheet slides from LEFT (side="left") to avoid occlusion.
//   For narrow modals or filters, side="right" is available but caller
//   must account for the sidebar offset (margin-right: 256px or similar).
//
// D-SHADCN-LAMTIX-WRAPPED-01: sole import path is @lamtix/ui
import * as React from 'react';
import { cn } from '../utils';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
} from '../shadcn/sheet';

// ── Re-export sub-parts with Lx prefix ───────────────────────────────────────

const LxSheetTrigger = SheetTrigger;
const LxSheetClose = SheetClose;
const LxSheetPortal = SheetPortal;
const LxSheetOverlay = SheetOverlay;

// ── LxSheetContent — fixed-pixel width, avoids logical max-w squeezing ────────

export interface LxSheetContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof SheetContent>, 'side'> {
  /** Physical side. Default 'left' (away from sidebar at physical right). */
  side?: 'left' | 'right' | 'top' | 'bottom';
  /** Fixed pixel width (side panels only). Default 600px. */
  sheetWidth?: number;
}

const LxSheetContent = React.forwardRef<
  React.ElementRef<typeof SheetContent>,
  LxSheetContentProps
>(({ side = 'left', sheetWidth = 600, className, style, children, ...props }, ref) => (
  <SheetContent
    ref={ref}
    side={side}
    style={{
      // Fixed pixel width — NOT logical max-w (prevents RTL squeezing)
      ...(side === 'left' || side === 'right' ? { width: `${sheetWidth}px`, maxWidth: '100vw' } : {}),
      ...style,
    }}
    className={cn(
      'border-border bg-background text-foreground',
      className,
    )}
    {...props}
  >
    {children}
  </SheetContent>
));
LxSheetContent.displayName = 'LxSheetContent';

const LxSheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <SheetHeader className={cn('text-start gap-2 pb-4 border-b border-border', className)} {...props} />
);
LxSheetHeader.displayName = 'LxSheetHeader';

const LxSheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <SheetFooter
    className={cn('pt-4 border-t border-border gap-2 sm:gap-0 sm:space-x-0 sm:justify-end', className)}
    {...props}
  />
);
LxSheetFooter.displayName = 'LxSheetFooter';

const LxSheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetTitle>,
  React.ComponentPropsWithoutRef<typeof SheetTitle>
>(({ className, ...props }, ref) => (
  <SheetTitle
    ref={ref}
    className={cn('text-start text-lg font-semibold text-foreground', className)}
    {...props}
  />
));
LxSheetTitle.displayName = 'LxSheetTitle';

const LxSheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetDescription>,
  React.ComponentPropsWithoutRef<typeof SheetDescription>
>(({ className, ...props }, ref) => (
  <SheetDescription
    ref={ref}
    className={cn('text-start text-sm text-muted-foreground', className)}
    {...props}
  />
));
LxSheetDescription.displayName = 'LxSheetDescription';

export {
  Sheet as LxSheet,
  LxSheetTrigger,
  LxSheetClose,
  LxSheetPortal,
  LxSheetOverlay,
  LxSheetContent,
  LxSheetHeader,
  LxSheetFooter,
  LxSheetTitle,
  LxSheetDescription,
};
